import {
  RANK_TIME_LABEL,
  SENIORITY_LABELS,
  companySizeLabel,
  companyStageLabel,
  label,
  type CandidateStatus,
} from '@remote-medium/shared';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router';

import { ConsentChip, DecisionChip } from '../../../lib/status-chip.tsx';
import { Card, DataTable, EmptyRow, ROW_LINK, SectionHeader, StatusRow } from '../../../lib/ui.tsx';
import { consentAsked, consentExpired, type CandidateMatch } from './context.ts';

function Consent({ m }: { m: CandidateMatch }) {
  const consent = m.candidateConsent;
  if (consent !== 'pending') return <ConsentChip status={consent} />;
  if (consentExpired(m)) return <span className="text-xs text-muted-foreground">Link expired</span>;
  if (consentAsked(m)) return <ConsentChip status="awaiting" />;
  return <span className="text-xs text-muted-foreground">Not asked</span>;
}

export function CandidateMatches({
  matches,
  loading,
  error,
  candidateStatus,
}: {
  matches: CandidateMatch[];
  loading: boolean;
  error: unknown;
  candidateStatus: CandidateStatus;
}) {
  const ready = !loading && !error;
  return (
    <Card className="gap-0 px-0 pb-0">
      <div className="px-5 pb-2">
        <SectionHeader
          title="Matches"
          actions={
            <span className="text-xs text-muted-foreground">
              Ranked nightly at {RANK_TIME_LABEL}
            </span>
          }
        />
      </div>
      <DataTable framed={false} minWidth="min-w-xl">
        <thead>
          <tr>
            <th>Role</th>
            <th className="text-right">Score</th>
            <th>Decision</th>
            <th>Consent</th>
            <th>
              <span className="sr-only">Open role</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={5} loading={loading} error={error} />
          {ready && matches.length === 0 && (
            <EmptyRow colSpan={5}>
              {candidateStatus === 'active'
                ? 'Not above the minimum score for any open role yet.'
                : 'Candidates are ranked once they are active.'}
            </EmptyRow>
          )}
          {ready &&
            matches.map((m) => {
              // Company name, then stage and size when the company gave them.
              const owner = [
                m.companyName,
                m.companyStage && companyStageLabel(m.companyStage),
                m.companySizeBand && companySizeLabel(m.companySizeBand),
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <tr key={m.id} className="hover:bg-muted/50">
                  <td>
                    <Link to={`/roles/${m.roleId}`} className={ROW_LINK}>
                      {m.roleTitle}
                      {m.roleSeniority && ` · ${label(SENIORITY_LABELS, m.roleSeniority)}`}
                    </Link>
                    {owner && <div className="text-muted-foreground">{owner}</div>}
                  </td>
                  <td className="text-right font-semibold tabular-nums">{m.systemScore}</td>
                  <td>
                    <DecisionChip status={m.curatorDecision} />
                  </td>
                  <td>
                    <Consent m={m} />
                  </td>
                  <td className="w-10 text-right">
                    <Link
                      to={`/roles/${m.roleId}`}
                      aria-label={`Open ${m.roleTitle}`}
                      className="inline-flex text-muted-foreground hover:text-foreground"
                    >
                      <ChevronRight aria-hidden="true" className="size-4" />
                    </Link>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </DataTable>
    </Card>
  );
}
